// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi, getGoogleAuthUrl, getAppleAuthUrl } from '../services/api';
import { LanguageProvider, useLang } from '../applicant/context/LanguageContext';
import { FormProvider } from '../applicant/context/FormContext';
import { ALL_DIAL_CODES, PRIORITY_CODES_SET, PRIORITY_DIAL_CODES } from '../constants/phoneDialCodes';
import { clearApplicantAuthSession, markApplicantSessionStarted } from '../utils/applicantSession';
import {
  getUserRole,
  rememberAuthPortal,
  resolvePostLoginPathAsync,
  resolveSessionRestorePathAsync,
} from '../utils/postLoginNavigation';
import {
  formatApiDetail,
  isMultipleProductionsError,
  projectsFromApiError,
} from '../utils/apiError';
import { getStoredPartnerToken } from '../utils/attribution';
import BrandLockup from '../components/BrandLockup';

const APPLICANT_ENTRY_INTENT_KEY = 'mvp_applicant_entry_intent';
const LOGIN_VIEW = { PHONE: 'phone', PROJECT: 'project', OTP: 'otp' };

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none" aria-hidden>
      <path d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z" fill="#FFC107" />
      <path d="M6.3 14.7l6.6 4.8C14.7 16 19.1 12 24 12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" fill="#FF3D00" />
      <path d="M24 44c5.4 0 10.3-2 14-5.2l-6.5-5.5C29.5 35.1 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8H6.3C9.6 35.6 16.2 44 24 44z" fill="#4CAF50" />
      <path d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.5C37.4 38.5 44 33.5 44 24c0-1.3-.1-2.7-.4-3.9z" fill="#1976D2" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 814 1000" fill="currentColor" aria-hidden>
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.8 135.4-317.5 269-317.5 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49.1 188.3-49.1 30.3 0 130.3 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

function flagToIso2(flag) {
  const parts = [...String(flag || '')];
  if (parts.length < 2) return '';
  const base = 0x1f1e6;
  const a = parts[0].codePointAt(0);
  const b = parts[1].codePointAt(0);
  if (!a || !b) return '';
  if (a < base || a > base + 25 || b < base || b > base + 25) return '';
  return String.fromCharCode((a - base) + 65, (b - base) + 65).toLowerCase();
}

function getCountryIso2(item) {
  const fromFlag = flagToIso2(item?.flag);
  if (fromFlag) return fromFlag;
  if (item?.code === '+1') return 'us';
  return 'un';
}

/** Resolve dial entry when several countries share one code (e.g. +1 US/CA). */
function resolveDialItem(countryCode, preferredIso) {
  const code = countryCode || '+34';
  const pool = [...PRIORITY_DIAL_CODES, ...ALL_DIAL_CODES];
  const matches = pool.filter((c) => c.code === code);
  if (!matches.length) return null;
  if (preferredIso) {
    const byIso = matches.find((c) => getCountryIso2(c) === preferredIso);
    if (byIso) return byIso;
  }
  if (code === '+1') {
    const us = matches.find((c) => getCountryIso2(c) === 'us');
    if (us) return us;
  }
  return matches[0];
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function splitInternationalPhone(rawValue, fallbackCountryCode) {
  const raw = String(rawValue || '').trim();
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, '');
  const withPlus = normalized.startsWith('+')
    ? normalized
    : normalized.startsWith('00')
      ? `+${normalized.slice(2)}`
      : '';
  if (!withPlus) return null;

  const sorted = [...ALL_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const { code } of sorted) {
    if (withPlus.startsWith(code)) {
      return {
        dial: code,
        national: digitsOnly(withPlus.slice(code.length)),
      };
    }
  }

  return {
    dial: fallbackCountryCode || '+34',
    national: digitsOnly(withPlus),
  };
}

function LoginHeader() {
  const navigate = useNavigate();
  return (
    <header className="w-full sticky top-0 z-40 bg-white" style={{ borderTop: '2px solid #a81c1c', borderBottom: '1px solid #e2e8f0' }}>
      <div className="px-5 h-14 flex items-center">
        <button type="button" onClick={() => navigate('/')} className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
          <BrandLockup
            containerClassName="flex items-center gap-3"
            logoClassName=""
            titleClassName="text-[14px] font-bold text-slate-900 tracking-tight whitespace-nowrap"
            subtitleClassName=""
            textWrapperClassName=""
            title="Global Mobility"
            showSubtitle={false}
          />
        </button>
      </div>
    </header>
  );
}

function LoginScreen() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [countryCode, setCountryCode] = useState('+34');
  const [phone, setPhone] = useState('');
  const [showDial, setShowDial] = useState(false);
  const [dialSearch, setDialSearch] = useState('');
  const [selectedIso, setSelectedIso] = useState(null);
  const dialRef = useRef(null);
  const [view, setView] = useState(LOGIN_VIEW.PHONE);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [projectChoices, setProjectChoices] = useState([]);
  const [loginOrgRef, setLoginOrgRef] = useState('');
  const [loginCrewToken, setLoginCrewToken] = useState('');

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const err = params.get('error') || params.get('e');
      if (!err) return;
      const messages = {
        account_not_found: 'No account found for that Google or Apple sign-in. Use the phone you registered with, or create an account first.',
        oauth_missing_token: 'Sign-in could not be completed. Please try again.',
        access_denied: 'Sign-in was cancelled. Please try again.',
        token_exchange_failed: 'Sign-in failed. Please try again.',
        email_not_provided: 'Your provider did not share an email. Please use phone sign-in.',
      };
      setError(messages[err] || 'Sign-in failed. Please try again.');
      params.delete('error');
      params.delete('e');
      params.delete('intent');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', next);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dialRef.current && !dialRef.current.contains(e.target)) {
        setShowDial(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const id = setTimeout(() => setResendTimer((v) => v - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  const currentFlag = useMemo(
    () => resolveDialItem(countryCode, selectedIso) || PRIORITY_DIAL_CODES[0],
    [countryCode, selectedIso]
  );
  const currentIso = getCountryIso2(currentFlag);

  const selectDialCountry = (c) => {
    setSelectedIso(getCountryIso2(c));
    setCountryCode(c.code);
    setShowDial(false);
    setDialSearch('');
  };

  const filtered = useMemo(() => {
    const q = dialSearch.trim().toLowerCase();
    if (!q) return ALL_DIAL_CODES.filter((c) => !PRIORITY_CODES_SET.has(c.code));
    return ALL_DIAL_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.includes(q) ||
        String(c.iso2 || '').toLowerCase().includes(q)
    );
  }, [dialSearch]);

  const fullPhone = `${countryCode}${String(phone || '').replace(/\D/g, '')}`;

  const persistLoginProjectChoice = (choice) => {
    const org = (choice?.customer_signup_ref || '').trim();
    const crew = (choice?.crew_invite_token || '').trim();
    setLoginOrgRef(org);
    setLoginCrewToken(crew);
    try {
      if (org) sessionStorage.setItem('mvp_customer_org_ref', org);
      else sessionStorage.removeItem('mvp_customer_org_ref');
      if (crew) sessionStorage.setItem('mvp_customer_crew_token', crew);
      else sessionStorage.removeItem('mvp_customer_crew_token');
      if (choice?.project_name) {
        sessionStorage.setItem('mvp_customer_org_name', String(choice.project_name));
      }
    } catch {
      /* ignore */
    }
  };

  const clearLoginProjectChoice = () => {
    setLoginOrgRef('');
    setLoginCrewToken('');
    setProjectChoices([]);
    try {
      sessionStorage.removeItem('mvp_customer_org_ref');
      sessionStorage.removeItem('mvp_customer_crew_token');
    } catch {
      /* ignore */
    }
  };

  const openProductionPicker = (projects) => {
    setProjectChoices(projects || []);
    setView(LOGIN_VIEW.PROJECT);
    setError('');
  };

  const requestOtpForPhone = async (orgRef, crewToken) => {
    setResendTimer(30);
    setLoginOrgRef(orgRef || '');
    setLoginCrewToken(crewToken || '');
    await mvpApi.requestPhoneOtp(
      fullPhone,
      'sms',
      'login',
      orgRef || null,
      crewToken || null,
    );
    setView(LOGIN_VIEW.OTP);
  };

  /** Normal typing: national digits only. */
  const applyNationalDigits = (raw) => {
    setError('');
    setPhone(digitsOnly(raw));
  };

  /**
   * Paste of a full international number → set dial code + national part.
   * e.g. +919154353454 → +91 / 9154353454
   */
  const applyPastedPhoneValue = (raw) => {
    setError('');
    const text = String(raw || '').trim();
    if (!text) {
      setPhone('');
      return;
    }

    let split = splitInternationalPhone(text, countryCode);
    if (!split && digitsOnly(text).length >= 10) {
      split = splitInternationalPhone(`+${digitsOnly(text)}`, countryCode);
    }
    if (split?.dial && split.national) {
      setCountryCode(split.dial);
      setPhone(split.national);
      return;
    }

    setPhone(digitsOnly(text));
  };

  const finishLogin = async (payload) => {
    const token = payload?.token;
    const user = payload?.user;
    if (!token) {
      setError('Invalid login response.');
      return;
    }
    localStorage.setItem('authToken', token);
    if (user) localStorage.setItem('userData', JSON.stringify(user));
    rememberAuthPortal('phone', user);
    const role = getUserRole(user);
    if (role !== 'advisor' && role !== 'admin' && role !== 'super_admin') {
      markApplicantSessionStarted();
      sessionStorage.setItem(APPLICANT_ENTRY_INTENT_KEY, 'login');
    }
    const dest = await resolvePostLoginPathAsync({
      user,
      method: 'phone',
    });
    navigate(dest, { replace: true });
  };

  const handleSendCode = async () => {
    if (!String(phone || '').trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const listed = await mvpApi.listParticipantPhoneProjects(fullPhone, 'login');
      const projects = Array.isArray(listed?.data?.projects) ? listed.data.projects : [];
      if (projects.length > 1) {
        openProductionPicker(projects);
        return;
      }
      if (projects.length === 1) {
        persistLoginProjectChoice(projects[0]);
        await requestOtpForPhone(
          projects[0].customer_signup_ref,
          projects[0].crew_invite_token,
        );
        return;
      }
      clearLoginProjectChoice();
      await requestOtpForPhone(null, null);
    } catch (err) {
      const embedded = projectsFromApiError(err);
      if (embedded?.length > 1 || isMultipleProductionsError(err)) {
        if (embedded?.length > 1) {
          openProductionPicker(embedded);
          return;
        }
        try {
          const listed = await mvpApi.listParticipantPhoneProjects(fullPhone, 'login');
          const projects = Array.isArray(listed?.data?.projects) ? listed.data.projects : [];
          if (projects.length > 1) {
            openProductionPicker(projects);
            return;
          }
        } catch {
          /* fall through */
        }
      }
      if (err?.response?.status === 404) setError('Phone number not registered.');
      else setError(formatApiDetail(err?.response?.data?.detail) || 'Failed to send code.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = async (choice) => {
    setLoading(true);
    setError('');
    try {
      persistLoginProjectChoice(choice);
      const existingCode = String(otp || '').trim();
      if (existingCode.length >= 4) {
        const partnerToken = getStoredPartnerToken();
        const res = await mvpApi.verifyPhoneOtp(
          fullPhone,
          existingCode,
          'login',
          null,
          partnerToken,
          null,
          choice.customer_signup_ref || null,
          choice.crew_invite_token || null,
        );
        await finishLogin(res?.data || {});
        return;
      }
      await requestOtpForPhone(choice.customer_signup_ref, choice.crew_invite_token);
    } catch (err) {
      if (err?.response?.status === 404) setError('Phone number not registered.');
      else setError(formatApiDetail(err?.response?.data?.detail) || 'Failed to send code.');
      setView(LOGIN_VIEW.PROJECT);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!String(otp || '').trim()) return;
    setLoading(true);
    setError('');
    try {
      let orgRef = loginOrgRef || null;
      let crewTok = loginCrewToken || null;
      if (!orgRef && !crewTok) {
        const listed = await mvpApi.listParticipantPhoneProjects(fullPhone, 'login');
        const projects = Array.isArray(listed?.data?.projects) ? listed.data.projects : [];
        if (projects.length > 1) {
          openProductionPicker(projects);
          return;
        }
        if (projects.length === 1) {
          persistLoginProjectChoice(projects[0]);
          orgRef = projects[0].customer_signup_ref || null;
          crewTok = projects[0].crew_invite_token || null;
        }
      }
      const partnerToken = getStoredPartnerToken();
      const res = await mvpApi.verifyPhoneOtp(
        fullPhone,
        String(otp).trim(),
        'login',
        null,
        partnerToken,
        null,
        orgRef,
        crewTok,
      );
      await finishLogin(res?.data || {});
    } catch (err) {
      const embedded = projectsFromApiError(err);
      if (embedded?.length > 1 || isMultipleProductionsError(err)) {
        if (embedded?.length > 1) {
          openProductionPicker(embedded);
          return;
        }
        try {
          const listed = await mvpApi.listParticipantPhoneProjects(fullPhone, 'login');
          const projects = Array.isArray(listed?.data?.projects) ? listed.data.projects : [];
          if (projects.length > 1) {
            openProductionPicker(projects);
            return;
          }
        } catch {
          /* fall through */
        }
      }
      setError(formatApiDetail(err?.response?.data?.detail) || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || loading) return;
    setOtp('');
    setError('');
    setLoading(true);
    try {
      await requestOtpForPhone(loginOrgRef || null, loginCrewToken || null);
    } catch (err) {
      const embedded = projectsFromApiError(err);
      if (embedded?.length > 1) {
        openProductionPicker(embedded);
        return;
      }
      if (err?.response?.status === 404) setError('Phone number not registered.');
      else setError(formatApiDetail(err?.response?.data?.detail) || 'Failed to send code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-[#F8F7F4] flex flex-col items-center px-6 py-6">
      <div className="w-full max-w-[400px] flex flex-col">
        <div className="mb-5 text-center">
          <h1 className="text-[26px] font-bold text-slate-900 mb-1.5">
            {view === LOGIN_VIEW.PROJECT ? 'Which production?' : t('auth.signIn')}
          </h1>
          <p className="text-[14px] text-slate-500 leading-relaxed">
            {view === LOGIN_VIEW.PROJECT
              ? 'This number is registered on more than one production. Choose where you want to continue.'
              : view === LOGIN_VIEW.OTP
                ? t('auth.subtitleOtp')
                : t('auth.subtitle')}
          </p>
        </div>

        {view === LOGIN_VIEW.PHONE && (
          <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4">
            <div ref={dialRef} className="relative mb-3">
              <div className={`flex h-12 bg-white border rounded-xl transition-all ${showDial ? 'border-[#1a7a3a]/50 ring-2 ring-[#1a7a3a]/20' : 'border-slate-200 focus-within:border-[#1a7a3a]/50 focus-within:ring-2 focus-within:ring-[#1a7a3a]/20'}`}>
                <button type="button" onClick={() => setShowDial((v) => !v)} className="h-full pl-3 pr-2.5 flex items-center gap-2 border-r border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap shrink-0 rounded-l-xl">
                  <span className={`fi fi-${getCountryIso2(currentFlag)} rounded-sm`} style={{ width: '1.1rem', height: '0.82rem' }} />
                  <span className="text-[13px] font-semibold text-slate-700">{countryCode}</span>
                  {showDial ? <i className="ri-arrow-up-s-line text-slate-400 text-xs" /> : <i className="ri-arrow-down-s-line text-slate-400 text-xs" />}
                </button>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => applyNationalDigits(e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    if (!pasted) return;
                    e.preventDefault();
                    applyPastedPhoneValue(pasted);
                  }}
                  placeholder={t('auth.phone')}
                  style={{ fontSize: '16px' }}
                  className="flex-1 min-w-0 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none bg-transparent rounded-r-xl"
                />
              </div>
              {showDial && (
                <div className="absolute top-[calc(100%+4px)] left-0 w-72 bg-white border border-slate-200 rounded-xl z-50 overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                  <div className="p-2 border-b border-slate-100">
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                      <i className="ri-search-line text-slate-400 text-sm" />
                      <input type="text" value={dialSearch} onChange={(e) => setDialSearch(e.target.value)} placeholder="Buscar país o código…" autoFocus style={{ fontSize: '16px', touchAction: 'manipulation' }} className="flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none" />
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {!dialSearch && <>
                      {PRIORITY_DIAL_CODES.map((c) => (
                        <button key={`p-${c.code}-${c.name}`} type="button" onClick={() => selectDialCountry(c)} className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${c.code === countryCode && getCountryIso2(c) === currentIso ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                          <span className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`} style={{ width: '1.1rem', height: '0.82rem' }} />
                          <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                          <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                        </button>
                      ))}
                      <div className="border-t border-slate-100 my-1" />
                    </>}
                    {filtered.length === 0 ? <div className="px-4 py-3 text-sm text-slate-400 text-center">Sin resultados</div> : filtered.map((c) => (
                      <button key={`a-${c.code}-${c.name}`} type="button" onClick={() => selectDialCountry(c)} className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${c.code === countryCode && getCountryIso2(c) === currentIso ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                        <span className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`} style={{ width: '1.1rem', height: '0.82rem' }} />
                        <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                        <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="button" onClick={handleSendCode} disabled={loading} className="w-full min-h-[48px] text-white font-semibold text-[15px] rounded-xl flex items-center justify-center disabled:opacity-50" style={{ background: '#1a7a3a' }}>
              {loading ? '...' : t('auth.sendCode')}
            </button>
          </div>
        )}

        {view === LOGIN_VIEW.PROJECT && (
          <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4">
            <div className="flex flex-col gap-2.5">
              {projectChoices.map((choice) => (
                <button
                  key={choice.project_id}
                  type="button"
                  disabled={loading}
                  onClick={() => handleSelectProject(choice)}
                  className="w-full text-left flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3.5 hover:border-[#1a7a3a]/40 hover:bg-white transition-colors disabled:opacity-50"
                >
                  <span className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[15px] shrink-0" aria-hidden>
                    🎬
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14.5px] font-semibold text-slate-900 truncate">
                      {choice.project_name}
                    </span>
                    <span className="block text-[13px] text-slate-500 truncate mt-0.5">
                      {[choice.organization_name, choice.status === 'registered' ? 'registered' : 'invited']
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-slate-400 text-lg shrink-0" aria-hidden />
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                clearLoginProjectChoice();
                setView(LOGIN_VIEW.PHONE);
                setOtp('');
                setError('');
              }}
              className="mt-3 text-[13px] text-slate-500 font-semibold"
            >
              {t('auth.changePhone')}
            </button>
          </div>
        )}

        {view === LOGIN_VIEW.OTP && (
          <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4">
            <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{t('auth.code')}</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder={t('auth.sixDigitCodePlaceholder')}
              className="login-field w-full min-h-[48px] mb-3 rounded-xl border border-slate-200 bg-white px-4 text-center tracking-[0.35em] text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-[#1a7a3a]/50 focus:ring-2 focus:ring-[#1a7a3a]/20"
              style={{ fontSize: '20px', backgroundColor: '#ffffff', color: '#0f172a' }}
            />
            <button type="button" onClick={handleVerify} disabled={loading || !String(otp || '').trim()} className="w-full min-h-[48px] text-white font-semibold text-[15px] rounded-xl flex items-center justify-center disabled:opacity-50" style={{ background: '#1a7a3a' }}>
              {loading ? '...' : t('auth.signIn')}
            </button>
            <div className="flex items-center justify-between mt-3">
              <button type="button" onClick={() => { setView(LOGIN_VIEW.PHONE); setOtp(''); setError(''); clearLoginProjectChoice(); }} className="text-[13px] text-slate-500 font-semibold">
                {t('auth.changePhone')}
              </button>
              {resendTimer > 0 ? (
                <p className="text-[13px] text-slate-500 whitespace-nowrap">{`${t('auth.resend')} (${resendTimer}s)`}</p>
              ) : (
                <button type="button" onClick={handleResend} disabled={loading} className="text-[13px] cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50" style={{ color: '#1a7a3a' }}>
                  {t('auth.resend')}
                </button>
              )}
            </div>
          </div>
        )}

        {view === LOGIN_VIEW.PHONE && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-[1.5px] bg-slate-200 rounded-full" />
              <span className="text-[13px] text-slate-400 font-bold tracking-[0.12em] uppercase bg-[#F8F7F4] px-2">{t('auth.or')}</span>
              <div className="flex-1 h-[1.5px] bg-slate-200 rounded-full" />
            </div>
            <div className="flex flex-col gap-3 mb-6">
              <button
                type="button"
                onClick={() => {
                  window.location.href = getGoogleAuthUrl('login', getStoredPartnerToken());
                }}
                className="w-full min-h-[48px] bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-medium text-[15px] rounded-xl flex items-center justify-center gap-3 cursor-pointer transition-colors whitespace-nowrap"
              >
                <GoogleIcon />
                {t('auth.google')}
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = getAppleAuthUrl('login', getStoredPartnerToken());
                }}
                className="w-full min-h-[48px] bg-slate-900 hover:bg-black text-white font-medium text-[15px] rounded-xl flex items-center justify-center gap-3 cursor-pointer transition-colors whitespace-nowrap"
              >
                <AppleIcon />
                {t('auth.apple')}
              </button>
            </div>
          </>
        )}

        {error ? <p className="text-red-600 text-sm text-center mb-3">{error}</p> : null}
        <p className="text-center text-[12px] text-slate-400 leading-relaxed">{t('auth.privacyNote')} <a href="https://globalmobility.law/politica-privacidad" target="_blank" rel="noopener noreferrer nofollow" className="text-slate-500 underline hover:text-slate-700 cursor-pointer transition-colors">{t('auth.privacyLink')}</a></p>
      </div>
    </div>
  );
}


function LoginPageContent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  /** false while validating an existing `authToken` so we do not flash the sign-in form. */
  const [sessionChecked, setSessionChecked] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      // OAuth error return - show the form + message, skip stale-token restore.
      if (params.get('error') || params.get('e')) return true;
    } catch {
      // ignore
    }
    return !localStorage.getItem('authToken');
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      if (params.get('error') || params.get('e')) {
        clearApplicantAuthSession();
        setSessionChecked(true);
        return;
      }
    } catch {
      // ignore
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setSessionChecked(true);
      return;
    }

    mvpApi
      .getMe()
      .then(async (res) => {
        const user = res.data?.user || res.data;
        if (user) localStorage.setItem('userData', JSON.stringify(user));
        sessionStorage.setItem(APPLICANT_ENTRY_INTENT_KEY, 'login');
        const dest = await resolveSessionRestorePathAsync(user);
        navigate(dest, { replace: true });
      })
      .catch(() => {
        clearApplicantAuthSession();
        setSessionChecked(true);
      });
  }, [navigate]);

  if (!sessionChecked) {
    return (
      <div className="min-h-screen w-full flex flex-col bg-[#F8F7F4] items-center justify-center px-6">
        <p className="text-slate-500 text-sm">{t('signingIn')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F8F7F4]">
      <LoginHeader />
      <div className="flex-1 flex md:items-start md:justify-center"><div className="w-full"><LoginScreen /></div></div>
    </div>
  );
}

export default function Login() {
  return (
    <LanguageProvider>
      <FormProvider>
        <LoginPageContent />
      </FormProvider>
    </LanguageProvider>
  );
}
